import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import * as _ from 'lodash';

import { ModalService } from 'src/app/components/modals/modal.service';

import { StateService, TransitionService } from '@uirouter/angular';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { BulkDeleteConfirmationComponent } from '../bulk-delete-confirmation/bulk-delete-confirmation.component';
import { BatchActionHandlerService, PaginatedScrollingListService, ScrollingListStateService } from '../services';

@Component({
    selector: 'batch-actions',
    templateUrl: './batch-actions.component.html',
    styleUrls: ['./batch-actions.component.scss'],
    standalone: false
})
export class BatchActionsComponent implements OnInit, OnDestroy {
  @Input() styles: string = 'xl:!tw-min-w-[850px]';

  additionalOperations: any[] = [];
  transitionHook: any;

  constructor(private stateService: StateService,
    private transitionService: TransitionService,
    private modalService: ModalService,
    private userStateService: UserStateService,
    protected batchService: BatchActionHandlerService,
    protected listService: PaginatedScrollingListService<any>,
    protected listStateService: ScrollingListStateService<any>
  ) {
  }

  ngOnInit(): void {
    this.batchService.operationsChanges().subscribe(() => {
      this._filterByRole();
      this._updateDeleteAction();

      this.additionalOperations = this.batchService.listOperations.operations
        .filter((op: any) => !op.showIn || op.showIn.includes('bulk'))
        .filter((op: any) => !op.mainOperation);
    });

    let _bypass = false;
    this.transitionHook = this.transitionService.onStart({}, (trans: any) => {
      if (_bypass) {
        _bypass = false;
        return;
      }
      if (this.listService.activeOperation) {
        var operationName = this.listService.activeOperation?.name?.toLowerCase();

        trans.abort();

        this.modalService.confirm('Cancel bulk ' + operationName + '?',
          'A bulk ' + operationName +
          ' is in progress. Navigating away from this page will cancel this action. Are you sure you want to cancel?',
          'Yes, Cancel',
          'No')
          .then(() => {
            this.batchService.cancel();

            _bypass = true;
            this.stateService.go(trans.to().name, trans.to().params);
          });
      }
    });

    window.onbeforeunload = (e: Event) => {
      if (this.listService.activeOperation) {
        e.preventDefault();
        e.returnValue = true;
      }
    };
  }

  ngOnDestroy(): void {
    window.onbeforeunload = undefined;

    this.transitionHook();
  }

  _filterByRole() {
    _.remove(this.batchService.listOperations.operations, (operation: any) => {
      return !this.userStateService.hasPermission(operation.requireRole);
    });
  }

  _updateDeleteAction() {
    _.each(this.batchService.listOperations.operations, (operation) => {
      if (operation.isDelete) {
        operation.beforeBatchAction = (selectedItems: any[]) => {
          return this.modalService.showDialog(BulkDeleteConfirmationComponent, {
            selectedItems: selectedItems,
            itemName: this.listService.activeOperation?.name
          }, { width: this.modalService.MODAL_SM });
        };
      }
    });
  }

  executeOperation(operation: any): void {
    if (operation && operation.onClick) {
      operation.onClick();
    }
  }

  hasAvailableOperations(): boolean {
    const mainOps = this.batchService.listOperations.operations
      ?.filter(op => op.mainOperation)
      ?.filter(op => !(op.hidden && op.hidden(this.listStateService.getSelected())))
      ?.length > 0;

    const additionalOps = this.additionalOperations
      ?.filter(op => !(op.hidden && op.hidden(this.listStateService.getSelected())))
      ?.length > 0;

    return mainOps || additionalOps;
  }

  getOperationIcon(operationName: string): string {
    const iconMap: { [key: string]: string } = {
      'Delete': 'trash',
      'Move': 'move-to-folder',
      'License': 'license',
      'Restart Rise Player': 'restart',
      'Reboot Media Player': 'reboot',
      'Update Firmware': 'update',
      'Export': 'export-all',
      'Export All': 'export-all',
      'Copy': 'duplicate',
      'Make a Copy': 'duplicate',
      'Screen Sharing': 'screen-share',
      'Set Monitoring': 'monitoring',
      'Set Reboot Time': 'reboot-time',
      'Set Address': 'set-address',
      'Enable Display Control': 'enable-display',
      'Disable Display Control': 'disable-display',
      'Move To Company': 'move-display',
      'Move To Trash': 'trash',
      'Assign Schedule': 'calendar',
      'Download': 'download',
      'Duplicate': 'duplicate',
      'Copy URL': 'copy',
      'Rename': 'edit'
    };

    return iconMap[operationName] || 'alert-triangle';
  }

  protected get filteredOperations() {
    return this.batchService.listOperations.operations
      .filter(op => !op.showIn || op.showIn.includes('bulk'))
      .filter(op => !(op.hidden && op.hidden(this.listStateService.getSelected())))
      || [];
  }

}
